/**
 * Restores the original question group order from a saved draft, counteracting
 * the backend's per-request Fisher-Yates shuffle so that sequential question
 * IDs (1, 2, 3…) always map to the same questions across fetches.
 *
 * @module reorderSimulationData
 */

import { optionText, type OptionValue } from '@/lib/questionOptions';
import type { SimulationData } from '@/types/exam-simulation';

/**
 * Returns a copy of `data` with each section's `questionGroups` sorted so
 * questions appear in the order given by `questionOrder` (an ordered array of
 * `SimulationQuestion.id` UUID strings from the original session).
 *
 * Groups whose questions are entirely absent from `questionOrder` (e.g. newly
 * added questions) sort to the end, preserving relative order among unknowns.
 * The original `data` object is never mutated.
 *
 * @param data - Fresh `SimulationData` from the API (groups may be in any order)
 * @param questionOrder - Ordered list of question UUIDs as seen in the original
 *   session. Sourced from `draftPayload.questionOrder`.
 */
export function reorderSimulationData(
  data: SimulationData,
  questionOrder: string[],
): SimulationData {
  if (questionOrder.length === 0) {
    return data;
  }

  const orderMap = new Map<string, number>(
    questionOrder.map((id, idx) => [id, idx]),
  );

  return {
    ...data,
    sections: data.sections.map((section) => ({
      ...section,
      // Shallow-copy before sort so React Query's cached array is not mutated.
      questionGroups: [...section.questionGroups].sort((a, b) => {
        // Rank each group by the earliest position of any of its questions in
        // the stored order. Math.min(...[]) === Infinity, so empty groups and
        // groups not in questionOrder both sort to the end.
        const posA = Math.min(
          ...a.questions.map((q) => orderMap.get(q.id) ?? Infinity),
        );
        const posB = Math.min(
          ...b.questions.map((q) => orderMap.get(q.id) ?? Infinity),
        );
        return posA - posB;
      }),
    })),
  };
}

/**
 * Returns a copy of `data` with each question's `options` array sorted to
 * match the order recorded in the saved draft, so the letter labels the
 * learner sees on resume (A/B/C…) point to the exact same option texts they
 * saw before the interruption.
 *
 * Matching is by option text (uniformly available for both legacy string
 * options and tagged option objects), so the guarantee holds even if the
 * backend returns options in a different order on resume. Options not present
 * in `optionOrder` (e.g., newly added) sort to the end, preserving their
 * relative position.
 *
 * The original `data` object is never mutated.
 *
 * @param data - Fresh `SimulationData` from the API
 * @param optionOrder - Map of question UUID → array of option texts in the
 *   original display order. Sourced from `draftPayload.optionOrder`.
 */
export function reorderSimulationOptions(
  data: SimulationData,
  optionOrder: Record<string, string[]>,
): SimulationData {
  if (Object.keys(optionOrder).length === 0) {
    return data;
  }

  return {
    ...data,
    sections: data.sections.map((section) => ({
      ...section,
      questionGroups: section.questionGroups.map((group) => ({
        ...group,
        questions: group.questions.map((q) => {
          const order = optionOrder[q.id];
          if (!order || order.length === 0 || !Array.isArray(q.options) || q.options.length === 0) {
            return q;
          }
          const positionMap = new Map<string, number>(
            order.map((text, idx) => [text, idx]),
          );
          return {
            ...q,
            options: [...q.options].sort((a, b) => {
              const posA = positionMap.get(optionText(a as OptionValue)) ?? Infinity;
              const posB = positionMap.get(optionText(b as OptionValue)) ?? Infinity;
              return posA - posB;
            }),
          };
        }),
      })),
    })),
  };
}
